import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { db, type PracticeEntry } from "@/lib/db";
import { ALL_VOWS } from "@/data/vows";
import { EntryCard } from "@/components/journal/EntryCard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft,
  BookOpen,
  FileText,
  HandHeart,
  CheckCircle2,
} from "lucide-react";

// Build a lookup map for virtue names
const virtueNameMap: Record<number, string> = {};
for (const v of ALL_VOWS) {
  virtueNameMap[v.id] = v.de.title;
}

type FilterType = "all" | "positive" | "negative" | "promises";

function formatDateHeader(dateStr: string, t: (key: string) => string): string {
  const today = new Date().toISOString().split("T")[0];
  const yesterday = new Date(Date.now() - 86400000).toISOString().split("T")[0];
  if (dateStr === today) return t("journal.today");
  if (dateStr === yesterday) return t("journal.yesterday");
  return new Date(dateStr + "T00:00:00").toLocaleDateString("de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

interface DateGroup {
  date: string;
  label: string;
  entries: PracticeEntry[];
}

export function JournalHistoryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id ?? "";

  const [allEntries, setAllEntries] = useState<PracticeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>("all");

  // Load all entries
  const loadEntries = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    try {
      const entries = await db.practiceEntries
        .where("userId")
        .equals(userId)
        .reverse()
        .sortBy("completedAt");
      setAllEntries(entries);
    } catch (err) {
      console.error("Failed to load journal entries:", err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  // Stats
  const stats = useMemo(() => {
    const total = allEntries.length;
    const openPromises = allEntries.filter(
      (e) => e.type === "negative" && e.versprechenErfuellt !== true
    ).length;
    const fulfilledPromises = allEntries.filter(
      (e) => e.type === "negative" && e.versprechenErfuellt === true
    ).length;
    return { total, openPromises, fulfilledPromises };
  }, [allEntries]);

  // Filtered entries
  const filteredEntries = useMemo(() => {
    switch (filter) {
      case "positive":
        return allEntries.filter((e) => e.type === "positive");
      case "negative":
        return allEntries.filter((e) => e.type === "negative");
      case "promises":
        return allEntries.filter(
          (e) => e.type === "negative" && e.versprechenErfuellt !== true
        );
      default:
        return allEntries;
    }
  }, [allEntries, filter]);

  // Group by date
  const dateGroups: DateGroup[] = useMemo(() => {
    const groups: Map<string, PracticeEntry[]> = new Map();
    for (const entry of filteredEntries) {
      const date = entry.date;
      if (!groups.has(date)) {
        groups.set(date, []);
      }
      groups.get(date)!.push(entry);
    }
    return Array.from(groups.entries()).map(([date, entries]) => ({
      date,
      label: formatDateHeader(date, t),
      entries,
    }));
  }, [filteredEntries, t]);

  // Actions
  const handleDelete = useCallback(
    async (id: string) => {
      await db.practiceEntries.delete(id);
      await loadEntries();
    },
    [loadEntries]
  );

  const handleFulfillPromise = useCallback(
    async (id: string) => {
      await db.practiceEntries.update(id, { versprechenErfuellt: true });
      await loadEntries();
    },
    [loadEntries]
  );

  const handleFulfillVersprechen = useCallback(
    async (id: string) => {
      await db.practiceEntries.update(id, { promiseErfuellt: true });
      await loadEntries();
    },
    [loadEntries]
  );

  // Filter buttons config
  const filters: { key: FilterType; label: string }[] = [
    { key: "all", label: t("journal.filter_all") },
    { key: "positive", label: `\u2713 ${t("journal.filter_positive")}` },
    { key: "negative", label: `\u2717 ${t("journal.filter_negative")}` },
    { key: "promises", label: t("journal.filter_promises") },
  ];

  if (loading) {
    return (
      <div className="px-4 py-6 space-y-6">
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-8 rounded" />
          <Skeleton className="h-8 w-48" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Skeleton className="h-20 rounded-lg" />
          <Skeleton className="h-20 rounded-lg" />
          <Skeleton className="h-20 rounded-lg" />
        </div>
        <div className="space-y-3">
          <Skeleton className="h-10 w-full rounded-lg" />
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="h-24 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 space-y-5 pb-24">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={() => navigate("/")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
          {t("journal.title")}
        </h2>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="border-gray-200 dark:border-[#1E3A5F]">
          <CardContent className="py-3 px-3 text-center">
            <div className="flex justify-center mb-1">
              <FileText className="h-4 w-4 text-vajra-blue dark:text-vajra-cyan" />
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.total}
            </p>
            <p className="text-xs text-gray-500 dark:text-muted-foreground">
              {t("journal.total")}
            </p>
          </CardContent>
        </Card>
        <Card className="border-gray-200 dark:border-[#1E3A5F]">
          <CardContent className="py-3 px-3 text-center">
            <div className="flex justify-center mb-1">
              <HandHeart className="h-4 w-4 text-orange-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.openPromises}
            </p>
            <p className="text-xs text-gray-500 dark:text-muted-foreground">
              {t("journal.open_promises")}
            </p>
          </CardContent>
        </Card>
        <Card className="border-gray-200 dark:border-[#1E3A5F]">
          <CardContent className="py-3 px-3 text-center">
            <div className="flex justify-center mb-1">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.fulfilledPromises}
            </p>
            <p className="text-xs text-gray-500 dark:text-muted-foreground">
              {t("journal.fulfilled")}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filter Buttons */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {filters.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={`
              whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-all border
              ${
                filter === f.key
                  ? "bg-vajra-blue/10 dark:bg-vajra-blue/20 border-vajra-blue/40 text-vajra-blue dark:text-vajra-cyan"
                  : "border-gray-200 dark:border-[#1E3A5F] text-gray-500 dark:text-muted-foreground hover:border-gray-400 dark:hover:border-gray-500"
              }
            `}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Entries grouped by date */}
      {dateGroups.length === 0 ? (
        <Card className="border-gray-200 dark:border-[#1E3A5F]">
          <CardContent className="py-12 flex flex-col items-center gap-3 text-center">
            <BookOpen className="h-12 w-12 text-gray-300 dark:text-muted-foreground/50" />
            <p className="text-gray-500 dark:text-muted-foreground">
              {t("journal.empty")}
            </p>
            <Button
              onClick={() => navigate("/entry")}
              className="mt-2 bg-vajra-blue hover:bg-vajra-blue/90"
            >
              {t("journal.empty_cta")}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {dateGroups.map((group) => (
            <div key={group.date} className="space-y-3">
              {/* Date header */}
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {group.label}
                </h3>
                <span className="text-xs text-gray-400 dark:text-muted-foreground">
                  {group.entries.length}{" "}
                  {group.entries.length === 1 ? "Eintrag" : "Einträge"}
                </span>
              </div>

              {/* Entry cards */}
              <div className="space-y-2">
                {group.entries.map((entry) => (
                  <EntryCard
                    key={entry.id}
                    entry={entry}
                    onDelete={handleDelete}
                    onFulfillPromise={handleFulfillPromise}
                    onFulfillVersprechen={handleFulfillVersprechen}
                    virtueNames={
                      entry.virtueIds?.map((id) => virtueNameMap[id]).filter(Boolean) as string[] | undefined
                    }
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
